import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { AuthService } from "../../services/auth/authService";
import { getCurrentUser } from "../../store/slices/authSlice";
import { toast } from "react-hot-toast";
import { User, Lock, Save, Bell, Shield } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHelpers";

export const AdminParametres: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("profile");
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  // Profile Form State
  const [profileData, setProfileData] = useState({
    entreprise_nom: user?.profile?.entreprise_nom || "",
    email: user?.email || "",
  });

  // Password Form State
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  // Notifications Form State
  const [notificationsData, setNotificationsData] = useState({
    notifications_email: user?.profile?.preferences?.notifications_email ?? true,
    notifications_sms: user?.profile?.preferences?.notifications_sms ?? false,
  });

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await AuthService.updateProfile(profileData);
      toast.success("Profil mis à jour");
      dispatch(getCurrentUser() as any);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    try {
      await AuthService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success("Mot de passe modifié");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
       toast.error(getErrorMessage(error));
    }
  };

  const handleNotificationsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const preferences = {
        ...(user?.profile?.preferences || {}),
        ...notificationsData,
      };
      await AuthService.updateProfile({ preferences });
      toast.success("Préférences de notifications mises à jour");
      dispatch(getCurrentUser() as any);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
        <p className="mt-1 text-sm text-gray-600">
          Gérez votre profil et les préférences de l'application
        </p>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab("profile")}
              className={`${
                activeTab === "profile"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center`}
            >
              <User className="h-4 w-4 mr-2" />
              Profil
            </button>
            <button
              onClick={() => setActiveTab("security")}
              className={`${
                activeTab === "security"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center`}
            >
              <Lock className="h-4 w-4 mr-2" />
              Sécurité
            </button>
            <button
              onClick={() => setActiveTab("notifications")}
              className={`${
                activeTab === "notifications"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center`}
            >
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab("system")}
                className={`${
                  activeTab === "system"
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center`}
              >
                <Shield className="h-4 w-4 mr-2" />
                Système
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === "profile" && (
            <form onSubmit={handleProfileUpdate} className="space-y-6 max-w-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom de l'entreprise / Utilisateur</label>
                <input
                  type="text"
                  value={profileData.entreprise_nom}
                  onChange={(e) => setProfileData({ ...profileData, entreprise_nom: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Save className="h-4 w-4 mr-2" /> Enregistrer
                </button>
              </div>
            </form>
          )}

          {activeTab === "security" && (
            <form onSubmit={handlePasswordUpdate} className="space-y-6 max-w-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700">Mot de passe actuel</label>
                <input
                  type="password"
                  required
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
                <input
                   type="password"
                   required
                   value={passwordData.new_password}
                   onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                   className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div>
                 <label className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
                 <input
                    type="password"
                    required
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border"
                 />
               </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-error-600 hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500"
                >
                  <Lock className="h-4 w-4 mr-2" /> Modifier le mot de passe
                </button>
              </div>
            </form>
          )}

          {activeTab === "notifications" && (
            <form onSubmit={handleNotificationsUpdate} className="space-y-6 max-w-lg">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="notifications_email"
                      type="checkbox"
                      checked={notificationsData.notifications_email}
                      onChange={(e) => setNotificationsData({ ...notificationsData, notifications_email: e.target.checked })}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="notifications_email" className="font-medium text-gray-700">Notifications par Email</label>
                    <p className="text-gray-500">Recevoir un email pour les événements importants (ex: nouvelle facture).</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="notifications_sms"
                      type="checkbox"
                      checked={notificationsData.notifications_sms}
                      onChange={(e) => setNotificationsData({ ...notificationsData, notifications_sms: e.target.checked })}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="notifications_sms" className="font-medium text-gray-700">Notifications par SMS</label>
                    <p className="text-gray-500">Recevoir des alertes critiques par SMS (bientôt disponible).</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Save className="h-4 w-4 mr-2" /> Enregistrer mes préférences
                </button>
              </div>
            </form>
          )}

          {activeTab === "system" && (
              <div className="text-center py-12 text-gray-500">
                  <p>Cette section sera bientôt disponible.</p>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};
